import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api'
import {
  ContributorData,
  MergeRequest,
  PipelineObject,
  Commit,
  RepositoryCreator,
  GitlabProject
} from '../components/types'
import { IssueObject } from './../components/types'
import {
  ContributorsSummary,
  GitlabCIApi,
  IssuesSummary,
  LanguagesSummary,
  MergeRequestsStatusSummary,
  MergeRequestsSummary,
  PipelineSummary,
  CommitsSummary
} from './GitlabCIApi'

export class GitlabCIClient implements GitlabCIApi {
  discoveryApi: DiscoveryApi
  identityApi: IdentityApi
  baseUrl: string
  constructor({
    discoveryApi,
    identityApi,
    baseUrl = 'https://gitlab.com/'
  }: {
    discoveryApi: DiscoveryApi
    identityApi: IdentityApi
    baseUrl?: string
  }) {
    this.discoveryApi = discoveryApi
    this.identityApi = identityApi
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
  }

  private async callApi<T>(
    path: string,
    query: { [key in string]: any }
  ): Promise<T | []> {
    const credentials = await this.identityApi.getCredentials()
    const token = credentials?.token || null
    const apiUrl = `${await this.discoveryApi.getBaseUrl('proxy')}/gitlabci`
    const response = await fetch(
      `${apiUrl}/${path}?${new URLSearchParams(query).toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    )
    if (response.status === 200) {
      return (await response.json()) as T
    }
    return []
  }

  async getPipelineSummary(projectID?: string): Promise<PipelineSummary | undefined> {
    const pipelineObjects = await this.callApi<PipelineObject[]>(
      'projects/' + projectID + '/pipelines',
      {}
    )
    let projectObj: any = await this.callApi<Object>('projects/' + projectID, {})
    if (pipelineObjects) {
      pipelineObjects.forEach((element: PipelineObject) => {
        element.project_name = projectObj?.name
      })
    }
    return {
      getPipelinesData: pipelineObjects!
    }
  }

  async getIssuesSummary(projectId: string): Promise<IssuesSummary | undefined> {
    const issuesObject = await this.callApi<IssueObject[]>(
      `projects/${projectId}/issues`,
      {}
    )

    let projectObj: any = await this.callApi<Object>('projects/' + projectId, {})
    if (issuesObject) {
      issuesObject.forEach((element: IssueObject) => {
        element.project_name = projectObj?.name
      })
    }

    return {
      getIssuesData: issuesObject!
    }
  }

  async getProjectName(projectID?: string): Promise<string | undefined> {
    let projectObj: any = await this.callApi<Object>('projects/' + projectID, {})
    return projectObj?.name
  }

  private async getUserProfilesData(
    contributorsData: ContributorData[]
  ): Promise<ContributorData[]> {
    for (let i = 0; contributorsData && i < contributorsData.length; i++) {
      const userProfile: any = await this.callApi<Object[]>('users', {
        search: contributorsData[i].email
      })
      if (userProfile) {
        userProfile.forEach((userProfileElement: ContributorData) => {
          if (userProfileElement.name == contributorsData[i].name) {
            contributorsData[i].avatar_url = userProfileElement?.avatar_url
          }
        })
      }
    }
    return contributorsData
  }

  async getMergeRequestsSummary(
    projectID?: string
  ): Promise<MergeRequestsSummary | undefined> {
    const mergeRquestsList = await this.callApi<MergeRequest[]>(
      'projects/' + projectID + '/merge_requests',
      {}
    )
    return {
      getMergeRequestsData: mergeRquestsList!
    }
  }

  async getMergeRequestsStatusSummary(
    projectID?: string,
    count?: number
  ): Promise<MergeRequestsStatusSummary | undefined> {
    const mergeRequestsList = await this.callApi<MergeRequest[]>(
      'projects/' + projectID + '/merge_requests',
      { per_page: count }
    )
    return {
      getMergeRequestsStatusData: mergeRequestsList!
    }
  }

  async getContributorsSummary(
    projectID?: string
  ): Promise<ContributorsSummary | undefined> {
    const contributorsData = await this.callApi<ContributorData[]>(
      'projects/' + projectID + '/repository/contributors',
      { sort: 'desc' }
    )
    const updatedContributorsData = await this.getUserProfilesData(contributorsData!)
    return {
      getContributorsData: updatedContributorsData!
    }
  }

  async getLanguagesSummary(projectID?: string): Promise<LanguagesSummary | undefined> {
    const languageObjects = await this.callApi<Object>(
      'projects/' + projectID + '/languages',
      {}
    )
    return {
      getLanguagesData: languageObjects!
    }
  }

  async getProjectDetails(projectSlug?: string): Promise<Object | undefined> {
    let projectDetails: any
    if (projectSlug) {
      projectDetails = await this.callApi<Object>(
        'projects/' + encodeURIComponent(projectSlug),
        {}
      )
    }
    return projectDetails
  }

  async retryPipelineBuild(
    projectID?: string,
    pipelineID?: string
  ): Promise<Object | undefined> {
    let retryBuild = await this.callApi<Object>(
      'projects/' + projectID + '/pipelines/' + pipelineID + '/retry',
      {}
    )
    return retryBuild
  }

  async getOldestCommits(projectID: string): Promise<CommitsSummary | undefined> {
    try {
      // Get project statistics to find total commits
      const project = await this.callApi<GitlabProject>(`projects/${projectID}`, {
        statistics: true
      })

      if (!project || Array.isArray(project)) {
        return { getCommitsData: [] }
      }

      const totalCommits = project.statistics?.commit_count

      if (!totalCommits || totalCommits === 0) {
        return { getCommitsData: [] }
      }

      // For small repos, just get all commits
      if (totalCommits <= 50) {
        const commits = await this.callApi<Commit[]>(
          `projects/${projectID}/repository/commits`,
          { per_page: totalCommits }
        )
        // Return in chronological order (oldest first)
        const oldestCommits = Array.isArray(commits)
          ? [...commits].reverse().slice(0, 10)
          : []
        return { getCommitsData: oldestCommits }
      }

      // For larger repos, paginate to the last page where oldest commits are
      const commitsPerPage = 50
      const lastPage = Math.ceil(totalCommits / commitsPerPage)

      const commits = await this.callApi<Commit[]>(
        `projects/${projectID}/repository/commits`,
        { per_page: commitsPerPage, page: lastPage }
      )

      // Reverse to get chronological order (oldest first) and take first 10
      const oldestCommits = Array.isArray(commits)
        ? [...commits].reverse().slice(0, 10)
        : []
      return { getCommitsData: oldestCommits }
    } catch (error) {
      return { getCommitsData: [] }
    }
  }

  private isBot(commit: Commit): boolean {
    const botNamePatterns =
      /bot|automation|ci-cd|deploy|machine user|developer portal|service/i
    const botEmailPatterns = /noreply|bot@|automation|service|devexp/i
    const mergeCommitPattern = /^Merge branch/

    return (
      botNamePatterns.test(commit.author_name) ||
      botEmailPatterns.test(commit.author_email) ||
      mergeCommitPattern.test(commit.title)
    )
  }

  private extractUsernameFromEmail(email: string): string | null {
    const sennderEmailPattern = /^([^@]+)@sennder\.com$/
    const match = email.match(sennderEmailPattern)
    return match ? match[1] : null
  }

  async getRepositoryCreator(projectID: string): Promise<RepositoryCreator | undefined> {
    const commitsSummary = await this.getOldestCommits(projectID)
    if (!commitsSummary || !commitsSummary.getCommitsData) {
      return {
        name: 'Unknown Creator',
        email: '',
        isExternal: false
      }
    }

    const commits = commitsSummary.getCommitsData

    // Find first non-bot commit
    for (const commit of commits) {
      if (!this.isBot(commit)) {
        const username = this.extractUsernameFromEmail(commit.author_email)
        return {
          name: commit.author_name,
          email: commit.author_email,
          userRef: username ? `user:default/${username}` : undefined,
          isExternal: !username
        }
      }
    }

    // No human commits found
    return {
      name: 'Unknown Creator',
      email: '',
      isExternal: false
    }
  }
}
